import { useCallback, useEffect, useState } from 'react';

import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { FaArrowLeft, FaPlay, FaWandMagicSparkles } from 'react-icons/fa6';
import { useHistory, useParams } from 'react-router-dom';
import { useApp } from 'common/AppContext';

import { CreateTestRailFromWorkItemModal } from './CreateTestRailFromWorkItemModal';
import { WorkItemHtml } from './WorkItemHtml';

const STATE_VARIANT = {
    Active: 'primary',
    Approved: 'info',
    Committed: 'info',
    Doing: 'primary',
    Done: 'success',
    New: 'secondary',
    Resolved: 'success'
};

export const AzureDevOpsWorkItem = () => {

    const { api } = useApp();
    const { id } = useParams();
    const history = useHistory();

    const [item, setItem] = useState(null);
    const [comments, setComments] = useState([]);
    const [updates, setUpdates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [planning, setPlanning] = useState(false);
    const [plan, setPlan] = useState(null);
    const [planError, setPlanError] = useState(null);
    const [editedPlan, setEditedPlan] = useState('');
    const [selectedEnvUrl, setSelectedEnvUrl] = useState('');
    const [showCreateCase, setShowCreateCase] = useState(false);

    const load = useCallback(async () => {

        setLoading(true);
        setError(null);

        try {

            const data = await api.get(`/api/azure-devops/work-items/${id}`);
            setItem(data?.item || null);
            setComments(data?.comments || []);
            setUpdates(data?.updates || []);

        } catch (e) {

            setError(e?.err || e?.message || String(e));

        } finally {

            setLoading(false);

        }

    }, [api, id]);

    useEffect(() => {

        load();

    }, [load]);

    const generatePlan = async () => {

        setPlanning(true);
        setPlanError(null);

        try {

            const data = await api.post(`/api/azure-devops/work-items/${id}/plan`);
            setPlan(data);
            setEditedPlan((data.steps || []).join('\n'));

            // Default the env to the first one of the matched app, if any.
            setSelectedEnvUrl(data.envs && data.envs.length ? data.envs[0].url : '');

        } catch (e) {

            setPlanError(e?.err || e?.message || String(e));

        } finally {

            setPlanning(false);

        }

    };

    const launch = () => {

        const planLines = editedPlan.split('\n').map(s => s.trim()).filter(Boolean);
        if (!planLines.length || !selectedEnvUrl) return;

        history.push('/feature-tester', {
            prefill: {
                appId: plan && plan.app ? plan.app.id : null,
                freeform: true,
                source: { id: item.id, title: item.title, type: 'azure_devops_work_item' },
                testPlan: planLines.join('\n'),
                url: selectedEnvUrl
            }
        });

    };

    if (loading)
        return (
            <Container className="py-4">
                <Spinner animation="border" />
            </Container>
        );

    if (error)
        return (
            <Container className="py-4">
                <Button className="mb-3" onClick={() => history.push('/azure-devops')} variant="outline-secondary">
                    <FaArrowLeft className="me-2" />
                    Back to issues
                </Button>
                <Alert variant="danger">
                    {error}
                </Alert>
            </Container>
        );

    return (

        <Container className="py-4">

            <Button className="mb-3" onClick={() => history.push('/azure-devops')} variant="outline-secondary">
                <FaArrowLeft className="me-2" />
                Back to issues
            </Button>

            <Row className="g-3">

                <Col lg={7}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body>

                            <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
                                <div>
                                    <div className="text-muted small">
                                        {item.type}
                                        {' '}
                                        #
                                        {item.id}
                                    </div>
                                    <h4 className="mb-1">
                                        {item.title}
                                    </h4>
                                    <Badge bg={STATE_VARIANT[item.state] || 'secondary'}>
                                        {item.state}
                                    </Badge>
                                </div>
                                <div className="d-flex gap-2 flex-shrink-0">
                                    <Button
                                        onClick={() => setShowCreateCase(true)}
                                        size="sm"
                                        variant="outline-primary"
                                    >
                                        <FaWandMagicSparkles className="me-1" />
                                        Create TestRail case
                                    </Button>
                                    {item.url && (
                                        <a className="btn btn-sm btn-outline-secondary" href={item.url} rel="noopener noreferrer" target="_blank">
                                            Open in ADO
                                        </a>
                                    )}
                                </div>
                            </div>

                            <MetadataGrid item={item} />

                            {Array.isArray(item.tags) && item.tags.length > 0 && (
                                <div className="mb-3">
                                    {item.tags.map(t => (
                                        <Badge bg="light" className="text-dark me-1" key={t} text="dark">
                                            {t}
                                        </Badge>
                                    ))}
                                </div>
                            )}

                            {item.description && (
                                <>
                                    <SectionHeader>
                                        Description
                                    </SectionHeader>
                                    <WorkItemHtml html={item.description} workItemId={item.id} />
                                </>
                            )}

                            {item.acceptanceCriteria && (
                                <>
                                    <SectionHeader>
                                        Acceptance Criteria
                                    </SectionHeader>
                                    <WorkItemHtml
                                        className="p-2 rounded border"
                                        html={item.acceptanceCriteria}
                                        style={{ background: 'rgba(22,163,74,0.04)' }}
                                        workItemId={item.id}
                                    />
                                </>
                            )}

                            {item.reproSteps && (
                                <>
                                    <SectionHeader>
                                        Repro steps
                                    </SectionHeader>
                                    <WorkItemHtml html={item.reproSteps} workItemId={item.id} />
                                </>
                            )}

                            {item.systemInfo && (
                                <>
                                    <SectionHeader>
                                        System info
                                    </SectionHeader>
                                    <WorkItemHtml className="text-muted small" html={item.systemInfo} workItemId={item.id} />
                                </>
                            )}

                            <ActivityTimeline
                                comments={comments}
                                updates={updates}
                                workItemId={item.id}
                            />

                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={5}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body>

                            <div className="d-flex align-items-center gap-2 mb-2">
                                <FaWandMagicSparkles className="text-primary" />
                                <h5 className="m-0">
                                    AI test plan
                                </h5>
                            </div>

                            {!plan ? (
                                <>
                                    <p className="text-muted small">
                                        Generate a tester-ready summary, mapped target app, and 3-8 actionable steps.
                                        You can edit them before launching.
                                    </p>
                                    {planError && (
                                        <Alert variant="danger">
                                            {planError}
                                        </Alert>
                                    )}
                                    <Button disabled={planning} onClick={generatePlan} variant="primary">
                                        {planning
                                            ? (
                                                <>
                                                    <Spinner animation="border" as="span" className="me-2" size="sm" />
                                                    Analyzing…
                                                </>
                                            )
                                            : 'Generate test plan'}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    {plan.summary && (
                                        <div className="mb-3 p-2 rounded border" style={{ background: 'rgba(0,0,0,0.02)' }}>
                                            <div className="text-muted small mb-1">
                                                Summary
                                            </div>
                                            <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
                                                {plan.summary}
                                            </div>
                                        </div>
                                    )}

                                    <Form.Group className="mb-3" controlId="ado-plan-env">
                                        <Form.Label>
                                            Environment
                                            {plan.app && (
                                                <span className="text-muted small ms-2">
                                                    (matched to
                                                    {' '}
                                                    {plan.app.name}
                                                    )
                                                </span>
                                            )}
                                        </Form.Label>
                                        {plan.envs && plan.envs.length > 0 ? (
                                            <Form.Select
                                                onChange={e => setSelectedEnvUrl(e.target.value)}
                                                value={selectedEnvUrl}
                                            >
                                                {plan.envs.map(env => (
                                                    <option key={env.id} value={env.url}>
                                                        {env.name}
                                                        {' '}
                                                        —
                                                        {env.url}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        ) : (
                                            <Alert className="mb-0" variant="warning">
                                                No environment matched. Pick one manually:
                                                <Form.Control
                                                    className="mt-2"
                                                    onChange={e => setSelectedEnvUrl(e.target.value)}
                                                    placeholder="https://dev.gmetrix.net"
                                                    type="url"
                                                    value={selectedEnvUrl}
                                                />
                                            </Alert>
                                        )}
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="ado-plan-steps">
                                        <Form.Label>
                                            Test steps
                                            <small className="text-muted ms-2">
                                                (one per line — edit freely)
                                            </small>
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            onChange={e => setEditedPlan(e.target.value)}
                                            rows={10}
                                            style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
                                            value={editedPlan}
                                        />
                                    </Form.Group>

                                    <div className="d-flex gap-2 justify-content-between">
                                        <Button onClick={generatePlan} size="sm" variant="outline-secondary">
                                            Regenerate
                                        </Button>
                                        <Button
                                            disabled={!editedPlan.trim() || !selectedEnvUrl}
                                            onClick={launch}
                                            variant="success"
                                        >
                                            <FaPlay className="me-2" />
                                            Launch in Feature Tester
                                        </Button>
                                    </div>
                                </>
                            )}

                        </Card.Body>
                    </Card>
                </Col>

            </Row>

            <CreateTestRailFromWorkItemModal
                onHide={() => setShowCreateCase(false)}
                show={showCreateCase}
                workItem={item}
            />

        </Container>

    );

};

const SectionHeader = ({ children }) => (
    <h6 className="text-uppercase text-muted small mt-3 mb-2" style={{ letterSpacing: 0.5 }}>
        {children}
    </h6>
);

/**
 * Compact key/value grid that surfaces the metadata fields the planner cares
 * about — area + iteration path, priority/severity, story points, dates,
 * authors. Hides any field that's missing so a sparse work item still looks
 * tidy.
 */
const MetadataGrid = ({ item }) => {

    const fmtDate = d => (d ? new Date(d).toLocaleDateString() : null);

    const rows = [
        ['Area path', item.areaPath && (
        <code>
            {item.areaPath}
        </code>
        )],
        ['Iteration', item.iterationPath && (
        <code>
            {item.iterationPath}
        </code>
        )],
        ['Assigned to', item.assignedTo],
        ['Created by', item.createdBy],
        ['Created', fmtDate(item.createdAt)],
        ['Last changed', fmtDate(item.changedAt)],
        ['Priority', item.priority],
        ['Severity', item.severity],
        ['Story points', item.storyPoints],
        ['Reason', item.reason],
        ['Found in build', item.foundInBuild]
    ].filter(([, v]) => v != null && v !== '');

    if (!rows.length) return null;

    return (
        <div className="text-muted small mb-3" style={{ display: 'grid', gap: '4px 12px', gridTemplateColumns: 'auto 1fr' }}>
            {rows.map(([k, v]) => (
                <div key={k} style={{ display: 'contents' }}>
                    <div className="text-uppercase" style={{ letterSpacing: 0.3 }}>
                        {k}
                    </div>
                    <div>
                        {v}
                    </div>
                </div>
            ))}
        </div>
    );

};

/**
 * Unified Activity timeline. Comments + significant work-item updates
 * (state transitions, assignment, edits) merged in chronological order
 * so the QA author can see the work item's evolution as a thread. The
 * AI planner / case-generator gets the same data via the backend so its
 * output reflects the latest negotiated scope, not just the original
 * description.
 */
const ActivityTimeline = ({ comments, updates, workItemId }) => {

    const entries = [];
    for (const c of comments || [])
        entries.push({ at: c.createdAt, comment: c, kind: 'comment' });
    for (const u of updates || [])
        entries.push({ at: u.revisedAt, kind: 'update', update: u });

    entries.sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));

    if (!entries.length) return null;

    const fmt = d => (d ? new Date(d).toLocaleString() : '');

    return (
        <>
            <h6 className="text-uppercase text-muted small mt-4 mb-2" style={{ letterSpacing: 0.5 }}>
                Activity (
                {entries.length}
                )
            </h6>
            <div className="d-flex flex-column gap-2">
                {entries.map((e, i) => (
                    e.kind === 'comment'
                        ? <ActivityComment author={e.comment.author} body={e.comment.body} key={`c-${e.comment.id}`} timestamp={fmt(e.at)} workItemId={workItemId} />
                        : <ActivityUpdate author={e.update.revisedBy} changes={e.update.changes} key={`u-${e.update.id}-${i}`} timestamp={fmt(e.at)} />
                ))}
            </div>
        </>
    );

};

const ACTIVITY_HEADER = 'd-flex align-items-center justify-content-between text-muted small mb-1';

const ActivityComment = ({ author, body, timestamp, workItemId }) => (
    <div className="border rounded p-2 bg-white">
        <div className={ACTIVITY_HEADER}>
            <strong className="text-body">
                {author || 'anon'}
            </strong>
            <span>
                {timestamp}
            </span>
        </div>
        <WorkItemHtml html={body} workItemId={workItemId} />
    </div>
);

const ActivityUpdate = ({ author, changes, timestamp }) => (
    <div
        className="border rounded p-2"
        style={{ background: 'rgba(0,0,0,0.015)', borderLeftColor: 'var(--bs-border-color)', borderLeftWidth: 3 }}
    >
        <div className={ACTIVITY_HEADER}>
            <span>
                <strong className="text-body">
                    {author || 'system'}
                </strong>
                <span className="ms-2 text-uppercase" style={{ fontSize: 10, letterSpacing: 0.5 }}>
                    edit
                </span>
            </span>
            <span>
                {timestamp}
            </span>
        </div>
        <ul className="mb-0 ps-3 small">
            {changes.map((ch, j) => (
                <li className="text-muted" key={j}>
                    <span className="text-body">
                        {ch.field}
                    </span>
                    {ch.from
                        ? (
                            <>
                                {': '}
                                <code>
                                    {ch.from}
                                </code>
                                {' → '}
                                <code>
                                    {ch.to}
                                </code>
                            </>
                        )
                        : (
                            <>
                                {' set to '}
                                <code>
                                    {ch.to}
                                </code>
                            </>
                        )}
                </li>
            ))}
        </ul>
    </div>
);
